<template>
  <div class="ops-overlay">
    <div class="ops-header">
      <h4 class="ops-title">OPS Console</h4>
      <div class="ops-search">
        <div class="input-group">
          <input v-model="query" class="form-control" placeholder="Search by email, company name, or ID..." @keyup.enter="search()">
          <div class="input-group-append">
            <button class="btn btn-primary" @click="search()" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm mr-1"></span>
              Search
            </button>
            <button class="btn btn-outline-secondary ml-2" @click="showCouponForm = !showCouponForm">Generate Coupon</button>
          </div>
        </div>
      </div>
    </div>

    <div class="ops-body">
      <div v-if="showCouponForm" class="ops-card">
        <h6 class="ops-section-title">Generate Coupon</h6>
        <div class="coupon-form">
          <div class="form-group mb-0">
            <label class="info-label">Discount (%)</label>
            <input v-model="couponDiscount" type="number" class="form-control form-control-sm" placeholder="0">
          </div>
          <div class="form-group mb-0">
            <label class="info-label">Free Days</label>
            <input v-model="couponFreeDays" type="number" class="form-control form-control-sm" placeholder="0">
          </div>
          <div class="form-group mb-0 coupon-note">
            <label class="info-label">Note</label>
            <input v-model="couponNote" type="text" class="form-control form-control-sm" placeholder="Internal note...">
          </div>
          <div class="coupon-action">
            <label class="info-label">&nbsp;</label>
            <button class="btn btn-sm btn-primary" @click="generateCoupon()" :disabled="loading">Generate</button>
          </div>
        </div>
        <div v-if="couponCode" class="coupon-result mt-2">
          <label class="info-label">Code</label>
          <div class="coupon-code">{{ couponCode }}</div>
        </div>
      </div>

      <div v-if="users.length > 0 && c === null" class="ops-card">
        <h6 class="ops-section-title">Users</h6>
        <div class="table-scroll-ops">
          <table>
            <thead><tr>
              <th>userId</th><th>email</th><th>userName</th><th>role</th><th>rights</th>
            </tr></thead>
            <tbody>
              <tr v-for="u in users" :key="u.userId">
                <td>{{ u.userId }}</td><td>{{ u.email }}</td><td>{{ u.userName }}</td><td>{{ u.role }}</td><td>{{ u.rights }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="c !== null">
        <div class="ops-card">
          <div class="ops-company-header">
            <div>
              <h5 class="mb-0">{{ c.company.companyName }}</h5>
              <small class="text-muted">ID: {{ c.company.companyId }} &nbsp;·&nbsp; {{ c.company.country }}</small>
            </div>
            <div class="ops-badges">
              <span :class="['badge', c.company.payingCustomer ? 'badge-success' : 'badge-secondary']">
                {{ c.company.payingCustomer ? 'Paying' : 'Not Paying' }}
              </span>
              <span v-if="c.company.partner" class="badge badge-info ml-1">Partner: {{ c.company.partner }}</span>
              <span class="badge badge-dark ml-1">{{ c.company.subscriptionType }}</span>
              <span class="badge badge-warning ml-1">{{ c.company.premiumType }}</span>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md-4">
              <div class="info-group">
                <div class="info-label">Address</div>
                <div>{{ c.company.companyAddress }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">VAT Number</div>
                <div>{{ c.company.vatNumber || '—' }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Bank Number</div>
                <div class="font-weight-bold">{{ c.company.bankNumber || '—' }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Currency</div>
                <div>{{ c.company.currency }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Time Zone</div>
                <div>{{ c.company.timeZone }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">USState</div>
                <div>{{ c.company.USState || '—' }}</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="info-group">
                <div class="info-label">Company Type</div>
                <div>{{ c.company.companyType }}<span v-if="c.company.companyTypeOther"> ({{ c.company.companyTypeOther }})</span></div>
              </div>
              <div class="info-group">
                <div class="info-label">Company State</div>
                <div>{{ c.company.companyState }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Fiscal Mode</div>
                <div>{{ c.company.fiscalMode }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Billing Version</div>
                <div>{{ c.company.billingVersion }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Created</div>
                <div>{{ c.company.created }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Last Changed</div>
                <div>{{ c.company.lastChanged }}</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="info-group">
                <div class="info-label">Google Billing Valid To</div>
                <div>{{ c.company.googleBillingValidTo || '—' }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Bank Billing Valid To</div>
                <div>{{ c.company.bankBillingValidTo || '—' }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Coupon Billing Valid To</div>
                <div>{{ c.company.couponBillingValidTo || '—' }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">GoPay Billing Valid To</div>
                <div>{{ c.company.goPayBillingValidTo || '—' }}</div>
              </div>
              <div class="info-group">
                <div class="info-label">Receipt Company Name</div>
                <div>{{ c.company.receiptCompanyName || '—' }}</div>
              </div>
            </div>
          </div>

          <div v-if="c.company.receiptHeader || c.company.receiptFooter" class="row mt-1">
            <div class="col-md-6" v-if="c.company.receiptHeader">
              <div class="info-group">
                <div class="info-label">Receipt Header</div>
                <div class="text-monospace small">{{ c.company.receiptHeader }}</div>
              </div>
            </div>
            <div class="col-md-6" v-if="c.company.receiptFooter">
              <div class="info-group">
                <div class="info-label">Receipt Footer</div>
                <div class="text-monospace small">{{ c.company.receiptFooter }}</div>
              </div>
            </div>
          </div>
          <div class="mt-3">
            <button class="btn btn-sm btn-warning" @click="openShadow()">Shadow</button>
          </div>
        </div>

        <div class="ops-card">
          <h6 class="ops-section-title">Users</h6>
          <div class="table-scroll-ops">
            <table>
              <thead><tr>
                <th>userId</th><th>email</th><th>userName</th><th>role</th><th>rights</th>
              </tr></thead>
              <tbody>
                <tr v-for="u in c.users" :key="u.userId">
                  <td>{{ u.userId }}</td><td>{{ u.email }}</td><td>{{ u.userName }}</td><td>{{ u.role }}</td><td>{{ u.rights }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="ops-card">
          <h6 class="ops-section-title">Devices <span class="badge badge-secondary ml-1">{{ c.devices.length }}</span></h6>
          <div class="table-scroll-ops">
            <table>
              <thead><tr>
                <th>androidId</th><th>modelName</th><th>name</th><th>appVersion</th><th>appVersionNum</th>
                <th>cashRegisterName</th><th>active</th><th>code</th><th>type</th><th>counter</th><th>edition</th><th>deviceMode</th>
              </tr></thead>
              <tbody>
                <tr v-for="d in c.devices" :key="d.deviceId">
                  <td>{{ d.androidId }}</td><td>{{ d.modelName }}</td><td>{{ d.name }}</td>
                  <td>{{ d.appVersion }}</td><td>{{ d.appVersionNum }}</td><td>{{ d.cashRegisterName }}</td>
                  <td><span :class="['badge', d.active ? 'badge-success' : 'badge-secondary']">{{ d.active ? 'Active' : 'Inactive' }}</span></td>
                  <td>{{ d.code }}</td><td>{{ d.type }}</td><td>{{ d.counter }}</td><td>{{ d.edition }}</td><td>{{ d.deviceMode }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="ops-card">
          <h6 class="ops-section-title">Last Receipts <span class="badge badge-secondary ml-1">{{ c.receipts.length }}</span></h6>
          <div class="table-scroll-ops">
            <table>
              <thead><tr>
                <th>id</th><th>deviceId</th><th>dateTime</th><th>cashRegister</th><th>receiptNumber</th>
                <th>code1</th><th>code2</th><th>total</th><th>currency</th><th>paymentType</th>
                <th>validity</th><th>createdAt</th><th>shiftCode</th><th>billName</th><th>fiscalCode</th><th></th>
              </tr></thead>
              <tbody>
                <tr v-for="r in c.receipts" :key="r.id">
                  <td>{{ r.id }}</td><td>{{ r.deviceId }}</td><td>{{ r.dateTime }}</td><td>{{ r.cashRegister }}</td>
                  <td>{{ r.receiptNumber }}</td><td>{{ r.code1 }}</td><td>{{ r.code2 }}</td><td>{{ r.total }}</td>
                  <td>{{ r.currency }}</td><td>{{ r.paymentType }}</td><td>{{ r.validity }}</td><td>{{ r.createdAt }}</td>
                  <td>{{ r.shiftCode }}</td><td>{{ r.billName }}</td><td>{{ r.fiscalCode }}</td>
                  <td><button class="btn btn-sm btn-outline-secondary" @click="pdfFile(r.id)">PDF</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="ops-card">
          <h6 class="ops-section-title">GoPay Payments <span class="badge badge-secondary ml-1">{{ c.goPayPayments.length }}</span></h6>
          <div class="table-scroll-ops">
            <table>
              <thead><tr>
                <th>contact</th><th>goPayId</th><th>amount</th><th>currency</th><th>orderNumber</th>
                <th>itemName</th><th>state</th><th>goPayUrl</th><th>goPayState</th><th>premiumDays</th>
                <th>userId</th><th>error</th><th>createdAt</th><th>paymentType</th><th>coupon</th>
                <th>recurrence</th><th>subscriptionType</th><th></th>
              </tr></thead>
              <tbody>
                <tr v-for="p in c.goPayPayments" :key="p.id">
                  <td>{{ p.contact }}</td><td>{{ p.goPayId }}</td><td>{{ p.amount }}</td><td>{{ p.currency }}</td>
                  <td>{{ p.orderNumber }}</td><td>{{ p.itemName }}</td>
                  <td><span :class="['badge', p.state === 'PAID' ? 'badge-success' : 'badge-warning']">{{ p.state }}</span></td>
                  <td>{{ p.goPayUrl }}</td><td>{{ p.goPayState }}</td><td>{{ p.premiumDays }}</td>
                  <td>{{ p.userId }}</td><td>{{ p.error }}</td><td>{{ p.createdAt }}</td><td>{{ p.paymentType }}</td>
                  <td>{{ p.coupon }}</td><td>{{ p.recurrence }}</td><td>{{ p.subscriptionType }}</td>
                  <td><button v-if="p.receiptId !== null" class="btn btn-sm btn-outline-secondary" @click="pdfFile(p.receiptId)">PDF</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="ops-card">
          <h6 class="ops-section-title">Bank Payments <span class="badge badge-secondary ml-1">{{ c.bankPayments.length }}</span></h6>
          <div class="table-scroll-ops">
            <table>
              <thead><tr>
                <th>bankBillingId</th><th>state</th><th>transactionAt</th><th>currency</th><th>amount</th>
                <th>vs</th><th>ss</th><th>clientMessage</th><th>userId</th><th>premiumDays</th><th></th>
              </tr></thead>
              <tbody>
                <tr v-for="b in c.bankPayments" :key="b.bankBillingId">
                  <td>{{ b.bankBillingId }}</td>
                  <td><span :class="['badge', b.state === 'PAID' ? 'badge-success' : 'badge-warning']">{{ b.state }}</span></td>
                  <td>{{ b.transactionAt }}</td><td>{{ b.currency }}</td><td>{{ b.amount }}</td>
                  <td>{{ b.vs }}</td><td>{{ b.ss }}</td><td>{{ b.clientMessage }}</td><td>{{ b.userId }}</td>
                  <td>{{ b.premiumDays }}</td>
                  <td><button v-if="b.receiptId !== null" class="btn btn-sm btn-outline-secondary" @click="pdfFile(b.receiptId)">PDF</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="ops-card">
          <h6 class="ops-section-title">Google Messages <span class="badge badge-secondary ml-1">{{ c.googleMessages.length }}</span></h6>
          <div class="table-scroll-ops">
            <table>
              <thead><tr>
                <th>id</th><th>createdAt</th><th>gid</th><th>state</th><th>jsonData</th>
                <th>errorMessage</th><th>subscriptionId</th><th>purchaseToken</th>
              </tr></thead>
              <tbody>
                <tr v-for="g in c.googleMessages" :key="g.id">
                  <td>{{ g.id }}</td><td>{{ g.createdAt }}</td><td>{{ g.gid }}</td>
                  <td><span :class="['badge', g.state === 'OK' ? 'badge-success' : 'badge-warning']">{{ g.state }}</span></td>
                  <td class="small text-monospace" style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" :title="g.jsonData">{{ g.jsonData }}</td>
                  <td>{{ g.errorMessage }}</td><td>{{ g.subscriptionId }}</td><td>{{ g.purchaseToken }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {baseURL} from '@/config/variables'

export default {
  name: 'Ops',

  data: function () {
    return {
      bu: baseURL,
      loading: false,
      showCouponForm: false,
      query: '',
      c: null,
      users: [],
      couponCode: '',
      couponDiscount: '',
      couponFreeDays: '',
      couponNote: ''
    }
  },

  methods: {
    search: function () {
      const vm = this;
      vm.loading = true;
      this.$http.post('/ops/ops-get-company-info', {query: vm.query})
          .then(function (response) {
            vm.c = response.data.company;
            vm.users = response.data.users;
            vm.loading = false;
          }, function () {
            vm.loading = false;
          });
    },
    generateCoupon: function () {
      const vm = this;
      vm.loading = true;
      this.$http.post('/ops/ops-generate-coupon', {discount: vm.couponDiscount, freeDays: vm.couponFreeDays, note: vm.couponNote})
          .then(function (response) {
            vm.couponCode = response.data.code;
            vm.loading = false;
          }, function () {
            vm.loading = false;
          });
    },
    openShadow: function() {
      let shUserName='';
      const vm = this;
      for(let i=0;i<vm.c.users.length;i++) {
        if(vm.c.users[i].role==='admin') {
          shUserName = vm.c.users[i].userName;
          break;
        }
      }
      window.open('https://app-sh.elementarypos.com/#/login?userName='+shUserName);
    },
    pdfFile: function(receiptId) {
      window.open(this.bu + '/doc/receipt/' + receiptId);
    },
  }
}
</script>

<style scoped>
.ops-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #f4f6f9;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ops-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #dee2e6;
  flex-shrink: 0;
}

.ops-title {
  margin: 0;
  font-weight: 700;
  color: #343a40;
  white-space: nowrap;
  font-size: 1rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.ops-search {
  flex: 1;
  max-width: 520px;
}

.ops-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.ops-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
}

.ops-company-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.ops-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
}

.ops-section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6c757d;
  margin-bottom: 12px;
}

.info-group {
  margin-bottom: 10px;
}

.info-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #adb5bd;
  margin-bottom: 1px;
}

.coupon-form {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.coupon-note {
  width: 200px;
}

.coupon-action {
  display: flex;
  flex-direction: column;
}

.coupon-result {
  display: inline-flex;
  flex-direction: column;
}

.coupon-code {
  font-family: monospace;
  font-size: 1rem;
  font-weight: 700;
  color: #28a745;
  background: #f0fff4;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  padding: 4px 10px;
  letter-spacing: 0.05em;
}

.table-scroll-ops {
  width: 100%;
  overflow-x: auto;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.table-scroll-ops table {
  width: auto;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.table-scroll-ops th {
  background: #f8f9fa;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
  padding: 8px 10px;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.table-scroll-ops td {
  padding: 7px 10px;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  color: #343a40;
}

.table-scroll-ops tbody tr:last-child td {
  border-bottom: none;
}

.table-scroll-ops tbody tr:hover {
  background: #f8f9fa;
}

.table-scroll-ops::-webkit-scrollbar {
  height: 6px;
}
.table-scroll-ops::-webkit-scrollbar-thumb {
  background: #ced4da;
  border-radius: 3px;
}
.table-scroll-ops::-webkit-scrollbar-track {
  background: #f8f9fa;
}
</style>
